import Highcharts from 'highcharts/esm/highcharts';

import 'highcharts/esm/modules/stock';
import 'highcharts/esm/highcharts-more';
import 'highcharts/esm/modules/variable-pie';
import 'highcharts/esm/modules/heatmap';

const THEME_CHART_BACKGROUNDS: Record<string, string> = {
  'theme-light': '#ffffff',
  'theme-dark': '#121212',
  'theme-green': '#C8ECD4',
  'theme-yellow': '#fff9c4',
  'theme-pink': '#F5C6CB',
  'theme-blue': '#D6E4F7',
};

const THEME_TEXT_COLORS: Record<string, string> = {
  'theme-light': '#333333',
  'theme-dark': '#ffffff',
  'theme-green': '#333333',
  'theme-yellow': '#333333',
  'theme-pink': '#333333',
  'theme-blue': '#333333',
};

function getActiveTheme(): string {
  const body = document.body;
  return (
    Object.keys(THEME_CHART_BACKGROUNDS).find((theme) => body.classList.contains(theme)) ??
    'theme-light'
  );
}

/** Stock chart zoom bar / navigator — tied to app theme, not OS dark mode */
function stockChartUiOptions(isDark: boolean, textColor: string, gridLineColor: string) {
  return {
    rangeSelector: {
      buttonTheme: {
        fill: isDark ? '#333333' : '#f7f7f7',
        stroke: isDark ? '#666666' : '#cccccc',
        style: { color: textColor, fontWeight: 'normal' },
        states: {
          hover: {
            fill: isDark ? '#444444' : '#e6e6e6',
            style: { color: textColor },
          },
          select: {
            fill: isDark ? '#555555' : '#e6ebf5',
            style: { color: textColor, fontWeight: 'bold' },
          },
        },
      },
      inputStyle: { color: textColor },
      labelStyle: { color: textColor },
    },
    navigator: {
      outlineColor: gridLineColor,
    },
    scrollbar: {
      barBackgroundColor: isDark ? '#404040' : '#cccccc',
      rifleColor: isDark ? '#cccccc' : '#666666',
      trackBackgroundColor: isDark ? '#1a1a1a' : '#f2f2f2',
      trackBorderColor: gridLineColor,
    },
  };
}

export function getHighchartsTextColor(theme?: string): string {
  return getThemeColors(theme).textColor;
}

function getThemeColors(theme?: string) {
  const activeTheme = theme ?? getActiveTheme();
  const backgroundColor = THEME_CHART_BACKGROUNDS[activeTheme] ?? '#ffffff';
  const textColor = THEME_TEXT_COLORS[activeTheme] ?? '#333333';
  const gridLineColor = activeTheme === 'theme-dark' ? '#444444' : '#e6e6e6';
  const isDark = activeTheme === 'theme-dark';
  return { backgroundColor, textColor, gridLineColor, isDark };
}

export function applyHighchartsTheme(theme?: string): void {
  const { backgroundColor, textColor, gridLineColor, isDark } = getThemeColors(theme);
  const stockUi = stockChartUiOptions(isDark, textColor, gridLineColor);
  const tooltipColors = {
    backgroundColor: isDark ? '#2a2a2a' : '#f5f5f5',
    borderColor: gridLineColor,
    style: { color: textColor },
  };

  Highcharts.setOptions({
    chart: {
      backgroundColor,
      plotBackgroundColor: backgroundColor,
      plotBorderWidth: 0,
      style: {
        fontFamily: 'inherit',
      },
    },
    ...stockUi,
    plotOptions: {
      pie: {
        borderWidth: 0,
      },
      series: {
        borderWidth: 0,
      },
    },
    title: {
      style: {
        color: textColor,
      },
    },
    xAxis: {
      labels: {
        style: {
          color: textColor,
        },
      },
      gridLineColor,
      lineColor: gridLineColor,
    },
    yAxis: {
      labels: {
        style: {
          color: textColor,
        },
      },
      gridLineColor,
      lineColor: gridLineColor,
    },
    legend: {
      itemStyle: {
        color: textColor,
      },
    },
    tooltip: tooltipColors,
  } as Highcharts.Options);

  const axisTheme = {
    labels: { style: { color: textColor } },
    gridLineColor,
    lineColor: gridLineColor,
    title: { style: { color: textColor } },
  };

  Highcharts.charts.forEach((chart) => {
    if (!chart) {
      return;
    }

    chart.update(
      {
        chart: {
          backgroundColor,
          plotBackgroundColor: backgroundColor,
          plotBorderWidth: 0,
        },
        legend: { itemStyle: { color: textColor } },
        tooltip: tooltipColors,
        ...stockUi,
      } as Highcharts.Options,
      false
    );

    chart.xAxis.forEach((axis) => axis.update(axisTheme, false));
    chart.yAxis.forEach((axis) => axis.update(axisTheme, false));
    chart.redraw(false);
  });
}

applyHighchartsTheme();

export default Highcharts;
